import log from "loglevel";

/**
 * SA-1216/SA-1217: código do link de vendas — `/signup?sales=<código>`.
 *
 * O código é OPACO e o client NÃO sabe o que ele significa: quem traduz
 * código→modo é o backend, contra o single type "Sales Entry Link". Isso é de
 * propósito — o parâmetro antigo (`?vendas=com-cartao`) revelava ao lead que
 * existe uma versão sem cartão, o que transforma a exigência do cartão em
 * alavanca de negociação. Como consequência, a palavra `with_card` não aparece
 * em lugar nenhum do browser do lead, nem no corpo das requests.
 */
const SEARCH_PARAM = "sales";
const STORAGE_KEY = "sales_entry";

/**
 * SA-1279: cupom de desconto — `/signup?promo=<código>`, combinável com o
 * `?sales=` ou sozinho. Mesma mecânica do código de link de vendas (opaco,
 * localStorage, uso único), por isso mora neste módulo em vez de num segundo
 * mecanismo com as mesmas regras. O que ele significa (percentual, ciclo,
 * brinde) só o backend sabe.
 */
const PROMO_SEARCH_PARAM = "promo";
const PROMO_STORAGE_KEY = "promo_code";

/**
 * O client não pode validar o código (só o servidor conhece os configurados),
 * mas limita formato e tamanho: o valor vem da URL e vai para o localStorage e
 * para o corpo do register.
 */
const CODE_MAX_LENGTH = 32;
const CODE_PATTERN = /^[a-z0-9-]+$/;

function normalizeCode(raw: string | null): string | null {
  if (typeof raw !== "string") return null;

  const code = raw.trim().toLowerCase();
  if (code.length === 0 || code.length > CODE_MAX_LENGTH) return null;

  return CODE_PATTERN.test(code) ? code : null;
}

/**
 * Espelha `persistTrackingParams()`: roda no boot do app e só escreve quando a
 * URL traz o parâmetro — não limpa o storage quando não traz. Quem consome
 * chama o `clear` correspondente (os valores são de uso único).
 */
function persistCodeFromSearch(searchParam: string, storageKey: string): void {
  try {
    if (typeof window === "undefined") return;
    const code = normalizeCode(
      new URLSearchParams(window.location.search).get(searchParam)
    );
    if (!code) return;
    window.localStorage.setItem(storageKey, code);
  } catch (error) {
    log.error(`Error persisting "${searchParam}" code from current URL`, error);
  }
}

function readCode(storageKey: string): string | null {
  try {
    if (typeof window === "undefined") return null;
    return normalizeCode(window.localStorage.getItem(storageKey));
  } catch {
    return null;
  }
}

function clearCode(storageKey: string): void {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.removeItem(storageKey);
  } catch (error) {
    log.error(`Error clearing "${storageKey}" code`, error);
  }
}

export function parseSalesEntryFromSearch(search: string): string | null {
  return normalizeCode(new URLSearchParams(search).get(SEARCH_PARAM));
}

export function persistSalesEntry(): void {
  persistCodeFromSearch(SEARCH_PARAM, STORAGE_KEY);
}

export function getSalesEntryCode(): string | null {
  return readCode(STORAGE_KEY);
}

export function persistPromoCode(): void {
  persistCodeFromSearch(PROMO_SEARCH_PARAM, PROMO_STORAGE_KEY);
}

export function parsePromoCodeFromSearch(search: string): string | null {
  return normalizeCode(new URLSearchParams(search).get(PROMO_SEARCH_PARAM));
}

export function getPromoCode(): string | null {
  return readCode(PROMO_STORAGE_KEY);
}

/**
 * Consome o cupom. Uso único pelo mesmo motivo do código de link de vendas: sem
 * isso um `/signup` limpo no mesmo browser (segunda conta, agência criando
 * várias) nasceria com o cupom de um link aberto antes.
 */
export function clearPromoCode(): void {
  clearCode(PROMO_STORAGE_KEY);
}

/** Query string atual, ou vazia fora do browser. */
function currentSearch(): string {
  return typeof window === "undefined" ? "" : window.location.search;
}

/**
 * Código efetivo: o que está no storage, ou o da URL desta página.
 *
 * A URL não é redundância. Ela cobre duas janelas em que o storage não responde:
 * o boot (o `persist*` roda num efeito do `AppProvider`, depois do primeiro
 * render) e o storage indisponível/cheio (modo privado, cota), onde o `persist`
 * falha em silêncio por decisão de projeto.
 *
 * Exibir e TRANSPORTAR usam esta mesma resolução de propósito: mostrar o selo do
 * cupom e depois cadastrar a conta sem ele seria prometer desconto e cobrar
 * cheio, que é o pior dos dois erros possíveis aqui.
 */
export function resolveSalesEntryCode(): string | null {
  return getSalesEntryCode() ?? parseSalesEntryFromSearch(currentSearch());
}

export function resolvePromoCode(): string | null {
  return getPromoCode() ?? parsePromoCodeFromSearch(currentSearch());
}

/**
 * Dimensão canônica de origem da conta, derivada da presença do código: não
 * existe link de vendas sem `origin = sales`. Usada como person/super property
 * nos dois entry-points de signup.
 *
 * Código com typo marca `sales` aqui e `self_serve` na Company — o client não
 * tem como saber se o código existe. A `Company.origin` é a fonte da verdade
 * (spec, seção de dados); esta property registra que a pessoa VEIO de um link de
 * vendas, mesmo que o link estivesse quebrado.
 */
export function resolveOrigin(): "sales" | "self_serve" {
  return resolveSalesEntryCode() ? "sales" : "self_serve";
}

/**
 * Consome o código. O valor é de USO ÚNICO: sem isso, um `/signup` limpo no
 * mesmo browser (segunda conta, logout e novo cadastro, agência criando várias
 * contas) nasceria `sales` com o código antigo. Chamado no SUCESSO do register e
 * do init — nunca no erro, senão uma falha de rede perde o código.
 */
export function clearSalesEntry(): void {
  clearCode(STORAGE_KEY);
}
